import { useState, useEffect, useMemo } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { type AuthUser } from "wasp/auth";
import {
  getAdminFaqItems,
  getAdminFaqCategories,
  createFaqItem,
  updateFaqItem,
  useQuery,
  useAction,
} from "wasp/client/operations";
import DefaultLayout from "../../layout/DefaultLayout";
import Breadcrumb from "../../layout/Breadcrumb";
import useTranslation from "../../../client/hooks/useTranslation";
import { useToast } from "../../../client/hooks/use-toast";
import { generateSlug } from "../../../blog/slug";
import { renderFaqMarkdown } from "../../../help/markdown";
import { Button } from "../../../client/components/ui/button";
import { Input } from "../../../client/components/ui/input";
import { Textarea } from "../../../client/components/ui/textarea";
import { Switch } from "../../../client/components/ui/switch";

interface Draft {
  categoryId: string;
  slug: string;
  slugTouched: boolean;
  questionFr: string;
  answerFr: string;
  questionEn: string;
  answerEn: string;
  keywords: string;
  sortOrder: number;
  isPublished: boolean;
}

interface CatOption {
  id: string;
  nameFr: string;
}

const EMPTY: Draft = {
  categoryId: "",
  slug: "",
  slugTouched: false,
  questionFr: "",
  answerFr: "",
  questionEn: "",
  answerEn: "",
  keywords: "",
  sortOrder: 0,
  isPublished: true,
};

export default function AdminHelpItemEditPage({ user }: { user: AuthUser }) {
  const t = useTranslation();
  const { toast } = useToast();
  const navigate = useNavigate();
  const { id } = useParams<{ id?: string }>();
  const isEdit = Boolean(id);

  const { data: cats } = useQuery(getAdminFaqCategories, undefined);
  // Reuse the list query to load the item being edited (avoids a dedicated getter).
  const { data: allItems } = useQuery(getAdminFaqItems, { status: "ALL" });
  const categories: CatOption[] = (cats as CatOption[]) ?? [];

  const createItem = useAction(createFaqItem);
  const updateItem = useAction(updateFaqItem);

  const [draft, setDraft] = useState<Draft>({ ...EMPTY });

  useEffect(() => {
    if (isEdit && allItems) {
      const found = (allItems as any[]).find((x) => x.id === id);
      if (found) {
        setDraft({
          categoryId: found.categoryId ?? found.category?.id ?? "",
          slug: found.slug,
          slugTouched: true,
          questionFr: found.questionFr,
          answerFr: found.answerFr,
          questionEn: found.questionEn ?? "",
          answerEn: found.answerEn ?? "",
          keywords: found.keywords ?? "",
          sortOrder: found.sortOrder,
          isPublished: found.isPublished,
        });
      }
    }
  }, [isEdit, allItems, id]);

  // Auto-slug from questionFr until the slug is manually edited.
  const effectiveSlug = draft.slugTouched ? draft.slug : generateSlug(draft.questionFr);

  const previewHtml = useMemo(() => renderFaqMarkdown(draft.answerFr), [draft.answerFr]);

  const save = async () => {
    const payload = {
      categoryId: draft.categoryId,
      slug: effectiveSlug,
      questionFr: draft.questionFr,
      answerFr: draft.answerFr,
      questionEn: draft.questionEn || null,
      answerEn: draft.answerEn || null,
      keywords: draft.keywords || null,
      sortOrder: Number(draft.sortOrder) || 0,
      isPublished: draft.isPublished,
    };
    try {
      if (isEdit) {
        await updateItem({ id: id!, ...payload });
      } else {
        await createItem(payload);
      }
      toast({ title: t.adminHelp.saved });
      navigate("/admin/help/questions");
    } catch (e: any) {
      toast({ title: e?.message ?? "Error", variant: "destructive" });
    }
  };

  return (
    <DefaultLayout user={user}>
      <Breadcrumb pageName={isEdit ? t.adminHelp.edit : t.adminHelp.newQuestion} />

      <div className="mx-auto max-w-3xl space-y-4">
        <label className="block text-sm text-muted-foreground">
          {t.adminHelp.category}
          <select
            value={draft.categoryId}
            onChange={(e) => setDraft({ ...draft, categoryId: e.target.value })}
            className="mt-1 w-full rounded border border-border bg-card px-3 py-2 text-sm text-foreground"
          >
            <option value="">—</option>
            {categories.map((c) => (
              <option key={c.id} value={c.id}>
                {c.nameFr}
              </option>
            ))}
          </select>
        </label>

        <label className="block text-sm text-muted-foreground">
          {t.adminHelp.questionFr}
          <Input value={draft.questionFr} onChange={(e) => setDraft({ ...draft, questionFr: e.target.value })} />
        </label>

        <label className="block text-sm text-muted-foreground">
          {t.adminHelp.slug}
          <Input
            value={effectiveSlug}
            onChange={(e) => setDraft({ ...draft, slug: e.target.value, slugTouched: true })}
          />
        </label>

        <div className="grid grid-cols-1 gap-4 lg:grid-cols-2">
          <label className="block text-sm text-muted-foreground">
            {t.adminHelp.answerFr}
            <Textarea
              rows={10}
              value={draft.answerFr}
              onChange={(e) => setDraft({ ...draft, answerFr: e.target.value })}
              className="font-mono text-xs"
            />
          </label>
          <div className="text-sm text-muted-foreground">
            {t.adminHelp.preview}
            <div
              className="prose prose-sm mt-1 min-h-[240px] max-w-none rounded border border-border bg-card p-3 text-muted-foreground [&_a]:text-primary [&_a]:underline [&_strong]:text-foreground [&_ul]:list-disc [&_ul]:pl-5 [&_ol]:list-decimal [&_ol]:pl-5"
              dangerouslySetInnerHTML={{ __html: previewHtml }}
            />
          </div>
        </div>

        <label className="block text-sm text-muted-foreground">
          {t.adminHelp.questionEn}
          <Input value={draft.questionEn} onChange={(e) => setDraft({ ...draft, questionEn: e.target.value })} />
        </label>

        <label className="block text-sm text-muted-foreground">
          {t.adminHelp.answerEn}
          <Textarea
            rows={6}
            value={draft.answerEn}
            onChange={(e) => setDraft({ ...draft, answerEn: e.target.value })}
            className="font-mono text-xs"
          />
        </label>

        <label className="block text-sm text-muted-foreground">
          {t.adminHelp.keywords}
          <Input value={draft.keywords} onChange={(e) => setDraft({ ...draft, keywords: e.target.value })} />
        </label>

        <div className="flex items-center gap-6">
          <label className="text-sm text-muted-foreground">
            {t.adminHelp.sortOrder}
            <Input
              type="number"
              value={draft.sortOrder}
              onChange={(e) => setDraft({ ...draft, sortOrder: Number(e.target.value) })}
              className="w-24"
            />
          </label>
          <label className="flex items-center gap-2 text-sm text-muted-foreground">
            <Switch checked={draft.isPublished} onCheckedChange={(v) => setDraft({ ...draft, isPublished: v })} />
            {draft.isPublished ? t.adminHelp.published : t.adminHelp.draft}
          </label>
        </div>

        <div className="flex gap-2">
          <Button onClick={save}>{t.adminHelp.save}</Button>
          <Button variant="ghost" onClick={() => navigate("/admin/help/questions")}>
            {t.adminHelp.cancel}
          </Button>
        </div>
      </div>
    </DefaultLayout>
  );
}
